import { Flex, Typography, colors } from '@aloft/toolkit-sui';

import { CartesianGrid, ComposedChart, Line, Scatter } from 'recharts';

import { GraphTooltipWrapper } from '../components/GraphTooltipWrapper.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import type { GraphProps } from '../types.ts';
import type { RegressionScatterPoint } from './types.ts';
import { computeBestFitLine } from './utils.ts';

type RegressionScatterGraphProps = GraphProps & {
  data: RegressionScatterPoint[];
  xKey: keyof RegressionScatterPoint;
  yKey: keyof RegressionScatterPoint;
  xLabel: string;
  yLabel: string;
  /** When true, removes the background and border-radius so the graph can live inside a shared styled container. */
  bare?: boolean;
  /** When true, renders a least-squares line of best fit over the scatter points. */
  showBestFitLine?: boolean;
  /** Optional formatter for x-axis tick labels. */
  xTickFormatter?: (value: number) => string;
  /** Set for discrete count fields so the x-axis never shows fractional ticks. */
  xAllowDecimals?: boolean;
};

const formatValue = (key: keyof RegressionScatterPoint, value?: number): string => {
  if (value === undefined || value === null) return '-';
  if (key === 'actualPrice' || key === 'predictedPrice') return `$${value.toLocaleString()}`;
  return value.toLocaleString();
};

export const RegressionScatterGraph = ({
  data,
  xKey,
  yKey,
  xLabel,
  yLabel,
  disableAnimations,
  width,
  height,
  bare = false,
  showBestFitLine = false,
  xTickFormatter,
  xAllowDecimals = true,
}: RegressionScatterGraphProps) => {
  const bestFitLineData = showBestFitLine ? computeBestFitLine(data, xKey, yKey) : [];

  return (
    <Flex
      flexDirection="column"
      width="100%"
      borderRadius="8px"
      backgroundColor="surface1"
      gap="1rem"
      paddingY="10px"
      paddingX="16px"
    >
      {showBestFitLine && <Typography variant="h3">Actual vs. Predicted Sale Price</Typography>}
      {showBestFitLine ? (
        <Typography variant="body2">
          Shows how well the model predicts actual sale prices. Points closer to the line of best
          fit indicate better predictions.
        </Typography>
      ) : (
        <Typography variant="body2">
          Select and view a single variable&apos;s relationship with price. Scatter around the trend
          line can be attributed to other variable&apos;s influence.
        </Typography>
      )}
      <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
        <ComposedChart
          width={width}
          height={height}
          margin={{ top: 20, left: 16, bottom: 8, right: 16 }}
          style={bare ? undefined : { background: colors.graphBackground, borderRadius: 8 }}
        >
          <CartesianGrid />
          <GraphXAxis
            dataKey={xKey}
            label={xLabel}
            type="number"
            name={xLabel}
            tickFormatter={xTickFormatter}
            allowDecimals={xAllowDecimals}
          />
          <GraphYAxis label={yLabel} dataKey={yKey} type="number" name={yLabel} />
          <GraphTooltipWrapper disableAnimations={disableAnimations}>
            {({ payload }) => {
              if (!payload?.length) return null;
              const point = (payload[0] as { payload: RegressionScatterPoint }).payload;
              return (
                <>
                  <Typography variant="body2">
                    {xLabel}: {formatValue(xKey, point[xKey])}
                  </Typography>
                  <Typography variant="body2">
                    {yLabel}: {formatValue(yKey, point[yKey])}
                  </Typography>
                </>
              );
            }}
          </GraphTooltipWrapper>
          <Scatter
            data={data}
            fill={colors.graphChart}
            isAnimationActive={!disableAnimations}
            line={false}
          />
          {showBestFitLine && bestFitLineData.length > 0 && (
            <Line
              data={bestFitLineData}
              dataKey={yKey}
              dot={false}
              activeDot={false}
              stroke={colors.graphChart2}
              strokeWidth={2}
              strokeDasharray="4 3"
              isAnimationActive={!disableAnimations}
              legendType="none"
            />
          )}
        </ComposedChart>
      </ResponsiveGraphWrapper>
    </Flex>
  );
};
